import { useEffect } from 'react';
import { useField, useFormikContext } from 'formik';
import { NSDropdown, NSLabel, NSSelect } from 'bricks';
import { IOptionValue } from 'bricks/types';
import RenderIf from 'components/RenderIf/RenderIf';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { useAttributeSuggested } from 'components/DealForm/hooks/useSuggestedAttribute';
import { DefaultAttributes } from '../../types';

interface DefaultAttributeDropdownProps<T extends string | number> {
  name: string;
  label: string;
  options: IOptionValue<T>[];
  attribute: DefaultAttributes;
  showSuggestedOnly: boolean;
  className?: string;
  onSelect?: (option: IOptionValue<T> | undefined, setFieldValue: (field: string, value: any) => void) => void;
  defaultValue?: T;
  additionalRenderCondition?: boolean;
  additionalRequiredCondition?: boolean;
  isLoading?: boolean;
  isDisabled?: boolean;
  shouldUseSelect?: boolean;
  isClearable?: boolean;
  customPlaceholder?: string;
}

const DefaultAttributeDropdown = <T extends string | number>({
    name,
    label,
    options,
    attribute,
    showSuggestedOnly,
    className = '',
    onSelect,
    defaultValue,
    additionalRenderCondition = true,
    additionalRequiredCondition = true,
    isLoading = false,
    isDisabled = false,
    shouldUseSelect = false,
    isClearable = false,
    customPlaceholder,
}: DefaultAttributeDropdownProps<T>) => {
    const [field, meta] = useField(name);
    const { setFieldValue } = useFormikContext();

    const { isRequired, shouldRender } = useAttributeSuggested({
        attribute,
        showSuggestedOnly,
        additionalRequiredCondition,
        additionalRenderCondition,
    });

    const selectedValue = field.value || defaultValue;
    const selectedOption = selectedValue ? options.find(option => option.value === selectedValue) : undefined;

    const handleSelectOption = (option: IOptionValue<T> | undefined) => {
        if (option) {
            setFieldValue(name, option.value);
            if (onSelect) {
                onSelect(option, setFieldValue);
            }
        } else {
            setFieldValue(name, undefined);
            if (onSelect) {
                onSelect(undefined, setFieldValue);
            }
        }
    };

    useEffect(() => {
        if (defaultValue && !field.value) {
            setFieldValue(name, defaultValue);
        }
    }, [defaultValue, field.value, setFieldValue, name]);

    return (
        <RenderIf isTrue={shouldRender}>
            <div className={className}>
                <NSLabel className="text-dark font-weight-normal">
                    {label}
                    {isRequired && <span className="text-danger"> *</span>}
                    {isLoading && (
                        <FontAwesomeIcon
                            icon={faSpinner}
                            spin
                            className="ml-2"
                            size="sm"
                        />
                    )}
                </NSLabel>
                <RenderIf isTrue={!shouldUseSelect}>
                    <NSDropdown
                        options={options}
                        selectedOption={selectedOption}
                        handleSelectOption={handleSelectOption}
                        isFullWidth
                        placeholder={isLoading ? 'Loading...' : customPlaceholder || 'Please select one'}
                        disabled={isLoading || isDisabled}
                    />
                </RenderIf>
                <RenderIf isTrue={shouldUseSelect}>
                    <NSSelect
                        name={name}
                        options={options}
                        value={selectedOption}
                        onChange={handleSelectOption}
                        placeholder={isLoading ? 'Loading...' : customPlaceholder || 'Please select one'}
                        isDisabled={isLoading || isDisabled}
                        isLoading={isLoading}
                        isClearable={isClearable}
                    />
                </RenderIf>
                {meta.touched && meta.error && <div className="text-danger">{meta.error}</div>}
            </div>
        </RenderIf>
    );
};

export default DefaultAttributeDropdown;
